import type { SidebarPrivileges } from '@ts-sidebar/interfaces'

import { useProfileStore } from '@stores/profile'

export const useSidebarPrivileges = (): SidebarPrivileges => {
  const profileStore = useProfileStore()

  const hasPrivilege = (key: string): ComputedRef<boolean> =>
    computed<boolean>(() => profileStore.privileges?.includes(key) ?? false)

  const canViewNewsletters = hasPrivilege('view-newsletters')
  const canManageOwnApiTokens = hasPrivilege('manage-own-api-tokens')
  const canManageAllApiTokens = hasPrivilege('manage-all-api-tokens')

  return {
    canViewCustomers: hasPrivilege('view-customers'),
    canSimpleViewDisciplines: hasPrivilege('simple-view-disciplines'),
    canViewSurveys: hasPrivilege('view-surveys'),
    canViewKanban: hasPrivilege('view-kanban'),
    canViewDemoBookings: hasPrivilege('view-demo-bookings'),
    canViewPaidBookings: hasPrivilege('view-paid-bookings'),
    canViewOrders: hasPrivilege('view-orders'),
    canManageOrders: hasPrivilege('manage-orders'),
    canViewSubscriptions: hasPrivilege('view-subscriptions'),
    canManageSubscriptions: hasPrivilege('manage-subscriptions'),
    canViewPromocodes: hasPrivilege('view-promocodes'),
    canManagePromocodes: hasPrivilege('manage-promocodes'),
    canViewTutors: hasPrivilege('view-tutors'),
    canViewTutorsSchedule: hasPrivilege('view-tutors-schedule'),
    canViewTutorTasks: hasPrivilege('view-tutor-tasks'),
    canViewTutorSlots: hasPrivilege('view-tutor-slots'),
    canViewTutorKanban: hasPrivilege('view-tutor-kanban'),
    canViewTutorPaymentPolicies: hasPrivilege('view-tutor-payment-policies'),
    canManageTutorPaymentPolicies: hasPrivilege('manage-tutor-payment-policies'),
    canViewSlotsManagement: hasPrivilege('view-slots-management'),
    canViewSlotsCoefficients: hasPrivilege('view-slots-coefficients'),
    canViewNewsletters,
    canViewNewsletterLogs: computed<boolean>(() =>
      (profileStore.privileges?.includes('view-newsletter-logs') ?? false) || canViewNewsletters.value,
    ),
    canViewUsers: hasPrivilege('view-users'),
    canManageUsers: hasPrivilege('manage-users'),
    canViewTeams: hasPrivilege('view-teams'),
    canManageTeams: hasPrivilege('manage-teams'),
    canManageApiTokens: computed<boolean>(() =>
      canManageOwnApiTokens.value || canManageAllApiTokens.value,
    ),
    canManageAllApiTokens,
    canViewAssessmentTemplates: hasPrivilege('view-assessment-templates'),
    canManageAssessmentTemplates: hasPrivilege('manage-assessment-templates'),
    canViewAssignmentTemplates: hasPrivilege('view-assignment-templates'),
    canManageAssignmentTemplates: hasPrivilege('manage-assignment-templates'),
    canViewSettings: hasPrivilege('view-settings'),
    canViewChangelog: hasPrivilege('view-changelog'),
    canImportSalesTax: hasPrivilege('import-sales-tax'),
    canImportCurriculum: hasPrivilege('import-curriculum'),
    canViewTutorPayments: hasPrivilege('view-tutor-payments'),
    canManageTutorPayments: hasPrivilege('manage-tutor-payments'),
    canManageTutorBilling: hasPrivilege('manage-tutor-billing'),
    canManageAvailability: hasPrivilege('manage-availability'),
  }
}
